import { useState, useEffect } from "react"
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material"
import { Event, RawServerEvent } from "@/common/types"
import { format } from "date-fns"

import { remapRawServerEvents } from "@/common/utils"
import useIsMobile from "@/hooks/useIsMobile"
import dayjs from "dayjs"
import { iconKey } from "@/common/globals"
// import useGetListEvents from "@/hooks/api/events/useGetListEvents"
import { useEventContext } from "@/contexts/EventContext"
import { useQueryClient } from "@tanstack/react-query"
import queryKeys from "@/api/queryKeys"
import useDialogState from "@/hooks/useDialogState"
import LargeEventDialog from "@/dialogs/events/LargeEventDialog"
import EventSeoMeta from "@/components/eventDetails/EventSeoMeta"
import shareIcon from "@/components/mapBox/images/copy_icon.png"
import useFavoriteEvent from "@/hooks/api/events/useFavoriteEvent"
import { useLocation, useNavigate } from "react-router-dom"
import { useSnackbar } from "@/contexts/SnackbarContext"
import LoginDialog from "@/dialogs/events/LoginDialog"
import { useGetMe } from "@/hooks/useGetMe"
import { HeartFilledIcon } from "@/common/images/HeartFilledIcon"
import { HeartIcon } from "@/common/images/HeartIcon"
import useIsTablet from "@/hooks/useIsTablet"
import useListPaginatedEvents from "@/hooks/api/events/useListPaginatedEvents"
import useInfiniteScroll from "@/hooks/useInfiniteScroll"
import ReactGA from "react-ga4"

const EventList = () => {
  const isMobile = useIsMobile()
  const isTablet = useIsTablet()
  const location = useLocation()
  const navigate = useNavigate()
  const { showSnackbar } = useSnackbar()
  const [descriptionIsExpanded, setDescriptionIsExpanded] = useState<number[]>(
    []
  )
  const { data: me } = useGetMe()

  const [eventIsOpen, setEventIsOpen] = useState<number[]>(() =>{
    const stored = sessionStorage.getItem("eventIsOpen")
    return stored ? JSON.parse(stored): []
  })
  const [events, setEvents] = useState([] as Event[])
  const queryClient = useQueryClient()
  const { value, searchVal, ageRangeFilter, petFriendlyFilter, priceFilter } =
    useEventContext()

  const {
    openDialog: openLoginDialog,
    closeDialog: closeLoginDialog,
    isOpen: loginIsOpen,
  } = useDialogState({})

  // read straight from the URL every render instead of copying it into
  // separate state - nothing to fall out of sync when the URL changes
  const activeSearchParams = new URLSearchParams(location.search)
  const openDialogId = activeSearchParams.get("details")
    ? Number(activeSearchParams.get("eventId"))
    : null

  const closeEventDialog = () => {
    navigate(location.pathname, { unstable_viewTransition: true })
  }

  const { mutateAsync: favoriteEvent } = useFavoriteEvent()

  const handleFavoriteEvent = (eventId: number) => {
    favoriteEvent(Number(eventId)).then((res) => {
      if (res.message === "Event favorited") {
        showSnackbar("Event Favorited", "success")
      } else {
        showSnackbar("Event removed from favorites", "error")
      }
    })
  }

  // matches the query-string deep link the app already uses to open an event -
  // keep click-through and share links pointing at the same working URL shape
  const buildEventDeepLink = (eventId: number) => {
    return `${location.pathname}?eventId=${eventId}&details=true`
  }

  const copyToClipBoard = async (eventId: number) => {
    try {
      const shareUrl = `${window.location.origin}${buildEventDeepLink(eventId)}`
      await navigator.clipboard.writeText(shareUrl)

      showSnackbar("Copied link to event!", "success")
    } catch (err) {
      console.error("could not copy", err)
    }
  }

  // const { data: eventResponse } = useGetListEvents({
  //   filters: {
  //     type: "list",
  //     page: 1,
  //     pageSize: 50,
  //     searchValue: searchVal,
  //     filterValue: value,
  //     ageRange: ageRangeFilter,
  //     petFriendly: petFriendlyFilter === "true" ? "true" : undefined,
  //     price: priceFilter,
  //   },
  // })

  const {
    data: paginatedEvents,
    isLoading,
    isSuccess,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
  } = useListPaginatedEvents({
    filters: {
      type: "list",
      // page: 1,
      pageSize: 15,
      searchValue: searchVal,
      filterValue: value,
      ageRange: ageRangeFilter,
      petFriendly: petFriendlyFilter === "true" ? "true" : undefined,
      price: priceFilter,
    },
  })

  const loadMoreRef = useInfiniteScroll(fetchNextPage, !!hasNextPage)

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.events.listPage({
        page: 1,
        pageSize: 10,
        searchValue: searchVal,
        filterValue: value,
        ageRange: ageRangeFilter,
        petFriendly: petFriendlyFilter,
        price: priceFilter,
      }).queryKey,
    })

    if (isSuccess && paginatedEvents) {
      const allEvents =
        paginatedEvents.pages.flatMap((page) => page.events) ?? []
      setEvents(remapRawServerEvents(allEvents as RawServerEvent[]))
    }
  }, [
    queryClient,
    paginatedEvents,
    isSuccess,
    searchVal,
    value,
    ageRangeFilter,
    petFriendlyFilter,
    priceFilter,
  ])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const eventId = searchParams.get("eventId")

    if (events.find((event) => event.event_id === Number(eventId))) {
      setEventIsOpen((prev) => {
        if (prev.includes(Number(eventId))) return prev
        return [...prev, Number(eventId)]
      })
    }
  }, [location.search, events])

  const toggleEvents = (id: number, eventTitle: string) => {
    ReactGA.event({
      category: "Event",
      action: "Click to expand event",
      label: `${eventTitle} | User: ${me?.id ?? "guest"}`,
      value: id,
    })
    setEventIsOpen((prevOpened) => {
      const updated = prevOpened.includes(id)
      ? prevOpened.filter((eventId) => eventId !== id)
      : [...prevOpened, id]
      sessionStorage.setItem("eventIsOpen", JSON.stringify(updated))
      return updated
    }
    )
  }

  const toggleDescriptions = (id: number) => {
    setDescriptionIsExpanded((prevOpened) =>
      prevOpened.includes(id)
        ? prevOpened.filter((eventId) => eventId !== id)
        : [...prevOpened, id]
    )
  }

  if (status === "pending") return <CircularProgress />
  if (isLoading && events.length === 0) {
    return (
      <>
        <CircularProgress />
      </>
    )
  }

  const eventBoxStyling = {
    padding: "1rem",
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
    color: "white",
  }

  const likeAndShareButtonStyling = {
    // backgroundColor: "white",
    color: "white",
    width: isMobile ? "30px" : "40px",
    height: isMobile ? "30px" : "40px",
    padding: ".5rem",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: ".2rem",
    marginRight: ".2rem",
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: isMobile ? "90%" : isTablet ? "80%" : "60%",
        maxHeight: "80vh",
        paddingBottom: "24px",
        overflowY: "auto",
      }}
    >
      {openDialogId && <EventSeoMeta eventId={openDialogId} />}
      {events.map((event) => {
        return (
          <Box
            key={event.event_id}
            sx={{
              display: "flex",
              cursor: "pointer",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              key={event.event_id}
              onClick={() => toggleEvents(event.event_id, event.title)}
              style={{
                padding: "0",
                display: "flex",
                borderRadius: "15px",
                alignItems: "center",
                justifyContent: "space-between",
                overflow: "hidden",
                height: "50px",
                width: "100%",
                marginTop: ".5rem",
                backgroundColor: "#0F1937",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#F0F2F5",
                  height: "100%",
                  width: "5rem",
                  color: "text.primary",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",

                    marginBottom: "-14px",
                  }}
                >
                  {format(event.event_start_timestamp, "dd")}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                  }}
                >
                  {format(event.event_start_timestamp, "MMM")}
                </div>
              </Box>
              {isMobile ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{ width: "100%", color: "white" }}
                >
                  <Typography fontWeight={400} fontSize={16} paddingBottom={1}>
                    {event.title.length > 25
                      ? `${event.title.slice(0, 25)}...`
                      : event.title.toUpperCase()}
                  </Typography>
                  <Typography fontWeight={400} fontSize={12}>
                    {event.location_title.toUpperCase()}
                  </Typography>
                </Box>
              ) : (
                <Box sx={eventBoxStyling}>
                  {event.title.toUpperCase()} at{" "}
                  {event.location_title.toUpperCase()}
                </Box>
              )}

              <Box
                sx={{
                  padding: isMobile ? "6px" : "1rem",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                }}
              >
                {event.event_types && (
                  <img
                    src={
                      // @ts-expect-error not sure how to resolve this
                      iconKey[event.event_types[0].machine_name] ||
                      "https://path/to/default-logo.png"
                    }
                    alt={event.event_types[0].human_name}
                    style={{
                      width: "30px",
                      height: "30px",
                      filter: "brightness(0) invert(1)",
                    }}
                  />
                )}
              </Box>
            </Button>
            {eventIsOpen.includes(event.event_id) && (
              <>
                {isMobile ? (
                  <Box
                    key={event.event_id}
                    onClick={() => navigate(buildEventDeepLink(event.event_id), { unstable_viewTransition: true })}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      borderBottomLeftRadius: "8px",
                      borderBottomRightRadius: "8px",
                      width: "90%",
                      maxHeight: "280px",
                      minHeight: "90px",
                    }}
                  >
                    <Stack
                      direction="row"
                      gap={1}
                      style={{
                        position: "absolute",
                        right: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "right",
                        width: "100%",
                        padding: ".3rem",
                        margin: ".3rem",
                        zIndex: "+1",
                      }}
                    >
                      <IconButton
                        style={likeAndShareButtonStyling}
                        onClick={(e) => {
                          e.stopPropagation()
                          copyToClipBoard(event.event_id)
                        }}
                      >
                        <img
                          style={{
                            width: "100%", // Make the image take the full width of the button
                            height: "100%", // Maintain aspect ratio
                            objectFit: "contain",
                            filter: "invert(1)",
                          }}
                          src={shareIcon}
                          alt="share event"
                        />
                      </IconButton>
                      <IconButton
                        style={likeAndShareButtonStyling}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (me.length) {
                            handleFavoriteEvent(event.event_id)
                          } else {
                            openLoginDialog({})
                          }
                        }}
                      >
                        {event?.favorited ? <HeartFilledIcon /> : <HeartIcon />}
                      </IconButton>
                    </Stack>
                    <img
                      src={
                        event?.event_images
                          ? event.event_images[0].image_url
                          : ""
                      }
                      alt={event.location_title}
                      style={{
                        width: "100%",
                        height: "90%",
                        display: "block",
                        objectFit: "cover",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: -20,
                        paddingTop: "2rem",
                        width: "100%",
                        backgroundImage:
                          "linear-gradient(0deg, rgba(2,0,36,1) 0%, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 90%)",
                      }}
                    >
                      <Stack sx={{ display: "flex", margin: "1rem" }}>
                        {/* event icon here */}
                        <Box display="flex">
                          {event.event_types && (
                            <img
                              // src={iconKey[event.event_types[0].machine_name]}
                              src={event.event_types[0].icon_link}
                              alt={event.event_types[0].human_name}
                              style={{
                                width: "18px",
                                height: "18px",
                                filter: "brightness(0) invert(1)",
                                paddingRight: "4px",
                              }}
                            />
                          )}
                          <Typography variant="h3" color="white">
                            {event.title.length > 23
                              ? `${event.title.slice(0, 23)}...`
                              : event.title}
                          </Typography>
                        </Box>
                        <style>
                          {`
                            .hide-scrollbar {
                              scrollbar-width: none; /* For Firefox */
                             }
                             .hide-scrollbar::-webkit-scrollbar {
                              display: none; /* For Chrome, Safari, and Opera */
                            }
                          `}
                        </style>
                        <Box
                          className="hide-scrollbar"
                          sx={{
                            overflowX: "scroll",
                            height: "32px",
                            width: "100%",
                          }}
                        >
                          <Typography
                            variant="h4"
                            color="white"
                            fontWeight={400}
                          >
                            {event.location_title},{" "}
                            {dayjs(event.event_start_timestamp).format(
                              "MMM, D - hA"
                            )}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    onClick={() => navigate(buildEventDeepLink(event.event_id), { unstable_viewTransition: true })}
                    sx={{
                      display: "flex",
                      width: "80%",
                      position: "relative",
                      backgroundColor: "#2C2C2C",
                      borderBottomLeftRadius: "10px",
                      borderBottomRightRadius: "10px",
                    }}
                  >
                    <img
                      src={
                        event.event_images
                          ? event.event_images[0].image_url
                          : ""
                      }
                      alt={event.location_title}
                      style={{
                        width: "140px",
                        height: "100%",
                        borderBottomLeftRadius: "10px",
                        objectFit: "cover",
                      }}
                    />
                    <Stack
                      alignContent="center"
                      justifyContent={"space-between"}
                      gap={1}
                      sx={{ width: "100%", padding: 1 }}
                    >
                      <Stack>
                        <Box
                          display="flex"
                          justifyContent={"space-between"}
                          alignItems={"center"}
                        >
                          <Typography variant="h2" color="white">
                            {event.title.length > 33
                              ? `${event.title.slice(0, 33)}...`
                              : event.title}
                          </Typography>
                          <Stack
                            direction="row"
                            gap={1}
                            style={{
                              right: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "right",
                              zIndex: "+5",
                            }}
                          >
                            <IconButton
                              style={likeAndShareButtonStyling}
                              onClick={(e) => {
                                e.stopPropagation()
                                copyToClipBoard(event.event_id)
                              }}
                            >
                              <img
                                style={{
                                  width: "100%", // Make the image take the full width of the button
                                  height: "100%", // Maintain aspect ratio
                                  objectFit: "contain",
                                  filter: "invert(1)",
                                }}
                                src={shareIcon}
                                alt="share event"
                              />
                            </IconButton>
                            <IconButton
                              style={likeAndShareButtonStyling}
                              onClick={(e) => {
                                e.stopPropagation()
                                if (me.length) {
                                  handleFavoriteEvent(event.event_id)
                                } else {
                                  openLoginDialog({})
                                }
                              }}
                            >
                              {event?.favorited ? (
                                <HeartFilledIcon />
                              ) : (
                                <HeartIcon />
                              )}
                            </IconButton>
                          </Stack>
                        </Box>
                        <Typography variant="body1" color="white">
                          {descriptionIsExpanded.includes(event.event_id)
                            ? event.description
                            : `${event.description.slice(0, 53)}...`}
                          {descriptionIsExpanded.includes(event.event_id) ? (
                            <span
                              style={{
                                display: "block", // Ensures it's on a new line
                                color: "#7CFFD0",
                                cursor: "pointer",
                                textDecoration: "underline",
                                marginTop: "5px", // Adds space for better readability
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleDescriptions(event.event_id)
                              }}
                            >
                              click to read less
                            </span>
                          ) : (
                            <span
                              style={{
                                color: "#7CFFD0",
                                cursor: "pointer",
                                textDecoration: "underline",
                                fontWeight: 300,
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                toggleDescriptions(event.event_id)
                              }}
                            >
                              click to view more
                            </span>
                          )}
                        </Typography>
                      </Stack>
                      <Stack gap={2}>
                        <Typography variant="body1" color="white">
                          {dayjs(event.event_start_timestamp).format("hh:mm A")}{" "}
                          -{" "}
                          <span style={{ fontStyle: "italic" }}>
                            {event.price}
                          </span>
                        </Typography>
                        <Typography variant="body1" color="white">
                          {event.location_title}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                )}
                {event.event_id === openDialogId && (
                  <LargeEventDialog
                    isOpen={true}
                    closeDialog={closeEventDialog}
                    eventId={event.event_id}
                  />
                )}
              </>
            )}
          </Box>
        )
      })}
      {hasNextPage && (
        <Box ref={loadMoreRef} textAlign="center" mt={2}>
          {isFetchingNextPage && <CircularProgress />}
        </Box>
      )}
      <LoginDialog
        closeDialog={closeLoginDialog}
        isOpen={loginIsOpen}
        message="Log in to favorite events!"
      />
    </div>
  )
}

export default EventList
